import { AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js/max';
import { environment } from '../../../environments/environment';

/**
 * List of supported country codes
 *
 * **Phase 2 Support**:
 * - US: United States
 * - CA: Canada
 * - GB: United Kingdom
 * - AU: Australia
 *
 * **Expansion Plan**:
 * - V1.5: Add EU countries (FR, DE, ES, IT, NL)
 * - V2.0: Global support (all countries)
 *
 * @see specs_and_project_plan/workitems/phase-2/WI-012b-auth-security-phase2.md
 */
const SUPPORTED_COUNTRIES = ['US', 'CA', 'GB', 'AU'];

/**
 * Custom Angular validator for phone numbers
 *
 * **Validation Rules**:
 * 1. Must start with '+'
 * 2. Must be valid E.164 format
 * 3. Must be a valid international number (libphonenumber-js)
 * 4. Must be from a supported country
 *
 * **Usage**:
 * ```typescript
 * this.phoneControl = new FormControl('', [
 *   Validators.required,
 *   phoneValidator()
 * ]);
 * ```
 *
 * **Error Messages**:
 * - invalidFormat: Phone number must start with +
 * - invalidPhone: Invalid phone number
 * - unsupportedCountry: Country not supported yet
 *
 * @returns ValidatorFn that returns null if valid, or error object if invalid
 */
export function phoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    // Allow empty values (use Validators.required separately)
    if (!control.value) {
      return null;
    }

    try {
      // Check for + prefix (E.164 requirement)
      if (!control.value.startsWith('+')) {
        return { invalidFormat: 'Phone number must start with +' };
      }

      // Validate using libphonenumber-js
      const isValid = isValidPhoneNumber(control.value);

      // Allow complete 555 numbers in emulator mode (test numbers)
      // Pattern: +1555 followed by exactly 7 digits (e.g., +15555550100)
      const is555Number = /^\+1555\d{7}$/.test(control.value);
      const allowTestNumbers = environment.useEmulators && is555Number;

      if (!isValid && !allowTestNumbers) {
        return { invalidPhone: 'Invalid phone number' };
      }

      // Parse and get country code
      const phoneNumber = parsePhoneNumber(control.value);

      // Check if country is supported
      if (phoneNumber.country && !SUPPORTED_COUNTRIES.includes(phoneNumber.country)) {
        return {
          unsupportedCountry: `Country ${phoneNumber.country} not supported yet. We currently support US, CA, GB, and AU.`
        };
      }

      // Valid phone number
      return null;
    } catch (error) {
      // Parsing failed - invalid format
      return { invalidPhone: 'Invalid phone number' };
    }
  };
}

/**
 * Sanitize phone number by removing non-numeric characters
 *
 * **Use Cases**:
 * - Clean user input before validation
 * - Format phone from copy-paste (e.g., "+1 (555) 123-4567" -> "+15551234567")
 *
 * **Preserves**:
 * - Leading '+' sign (required for E.164)
 * - All digits
 *
 * **Removes**:
 * - Spaces, parentheses, hyphens, dots
 * - Any other non-numeric characters
 *
 * @param phone - Raw phone number input
 * @returns Sanitized phone number (E.164 format)
 *
 * @example
 * sanitizePhoneNumber('+1 (555) 123-4567') // Returns: '+15551234567'
 * sanitizePhoneNumber('+44 20 7946 0958') // Returns: '+442079460958'
 */
export function sanitizePhoneNumber(phone: string): string {
  if (!phone) return '';

  // Remove all non-numeric except +
  // This regex keeps + and digits, removes everything else
  return phone.replace(/[^\d+]/g, '');
}

/**
 * Format phone number for display (masks actual number)
 *
 * **Security Note**:
 * - Shows only last 4 digits for privacy
 * - Used in UI to display verified phone without exposing full number
 * - Compliant with GDPR/CCPA data minimization
 *
 * **Format**:
 * - US: +1 *** *** 1234
 * - UK: +44 *** *** 5678
 * - Other: +XX *** *** 9012
 *
 * @param phone - E.164 phone number
 * @returns Masked phone number for display
 *
 * @example
 * formatPhoneForDisplay('+15551234567') // Returns: '+1 *** *** 4567'
 * formatPhoneForDisplay('+442079460958') // Returns: '+44 *** *** 0958'
 */
export function formatPhoneForDisplay(phone: string): string {
  if (!phone || phone.length < 4) {
    return phone;
  }

  try {
    // Parse phone to get country code
    const phoneNumber = parsePhoneNumber(phone);

    // Get last 4 digits
    const lastFour = phone.slice(-4);

    // Format with country code and masked middle
    return `+${phoneNumber.countryCallingCode} *** *** ${lastFour}`;
  } catch (error) {
    // Fallback if parsing fails
    const lastFour = phone.slice(-4);
    const countryCode = phone.slice(0, phone.length - 10);
    return `${countryCode} *** *** ${lastFour}`;
  }
}

/**
 * Get human-readable country name from phone number
 *
 * **Use Cases**:
 * - Display country in UI (e.g., "United States (+1)")
 * - Validation feedback (e.g., "French phone numbers not supported yet")
 *
 * @param phone - E.164 phone number
 * @returns Country name or 'Unknown'
 *
 * @example
 * getCountryName('+15551234567') // Returns: 'United States'
 * getCountryName('+442079460958') // Returns: 'United Kingdom'
 */
export function getCountryName(phone: string): string {
  try {
    const phoneNumber = parsePhoneNumber(phone);

    const countryNames: Record<string, string> = {
      US: 'United States',
      CA: 'Canada',
      GB: 'United Kingdom',
      AU: 'Australia',
      // Add more as countries are supported
    };

    return phoneNumber.country ? countryNames[phoneNumber.country] || phoneNumber.country : 'Unknown';
  } catch (error) {
    return 'Unknown';
  }
}

/**
 * Check if country is supported
 *
 * @param phone - E.164 phone number
 * @returns true if country is supported, false otherwise
 */
export function isCountrySupported(phone: string): boolean {
  try {
    const phoneNumber = parsePhoneNumber(phone);
    return phoneNumber.country ? SUPPORTED_COUNTRIES.includes(phoneNumber.country) : false;
  } catch (error) {
    return false;
  }
}
